import * as z from 'zod';
import { organizationRefSchema, readOrganizationRefSchema } from '../organizations';
import { readLeadRefSchema, leadRefSchema, importLeadItemSchema } from '../leads';
import {readCampaignRefSchema} from "../campaigns";
import {readJobSchema} from "../jobs";

// 1. Reference schema with label field (for dropdowns, selects, links)
export const readAudienceRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});
export type ReadAudienceRef = z.infer<typeof readAudienceRefSchema>;

// 2. Minimal reference schema (for foreign keys)
export const audienceRefSchema = z.object({
  id: z.uuid(),
});
export type AudienceRef = z.infer<typeof audienceRefSchema>;

// Statistics schema for audience metrics
export const audienceStatisticsSchema = z.object({
  leadsCount: z.number().nullable(),
  campaignsCount: z.number().nullable(),
});
export type AudienceStatistics = z.infer<typeof audienceStatisticsSchema>;

// 3. Read schema - all properties nullable (API response)
export const readAudienceSchema = z.object({
  id: z.uuid().nullable(),
  name: z.string().nullable(),
  leads: z.array(readLeadRefSchema).nullable(),
  organization: readOrganizationRefSchema.nullable(),
  statistics: audienceStatisticsSchema.nullable(),
  campaigns: z.array(z.lazy(() => readCampaignRefSchema)).nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
  isReady: z.boolean().nullable(),
  lastImportJob: z.lazy(() => readJobSchema).nullable(),
  // Opt-in field (fields=ip_match_rate); fraction 0-1, null without the audience read_sensitive permission
  ipMatchRate: z.number().nullable(),
});
export type ReadAudience = z.infer<typeof readAudienceSchema>;

// 4. Create schema - based on DTO serialization groups
export const createAudienceSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(250, 'Name must not exceed 250 characters'),
  organization: organizationRefSchema,
  leads: z.array(leadRefSchema).optional(),
});
export type CreateAudience = z.infer<typeof createAudienceSchema>;

// 5. Update schema - based on DTO serialization groups
export const updateAudienceSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(250, 'Name must not exceed 250 characters'),
  organization: organizationRefSchema,
  leads: z.array(leadRefSchema).optional(),
});
export type UpdateAudience = z.infer<typeof updateAudienceSchema>;

// 6. Patch schema - partial update
export const patchAudienceSchema = updateAudienceSchema.partial();
export type PatchAudience = z.infer<typeof patchAudienceSchema>;

// Legacy export for backward compatibility
export const audienceSchema = readAudienceSchema;
export type Audience = ReadAudience;

// Import context for audiences — supports either an existing audience id
// or a name (server creates the audience as part of the import).
export const audienceImportContextRefSchema = z
  .object({
    id: z.uuid().optional(),
    name: z.string().optional(),
  })
  .refine((v) => !!v.id || !!v.name, {
    message: 'audience.id or audience.name is required',
  });
export type AudienceImportContextRef = z.infer<typeof audienceImportContextRefSchema>;

export const importAudienceContextSchema = z.object({
  audience: audienceImportContextRefSchema,
});
export type ImportAudienceContext = z.infer<typeof importAudienceContextSchema>;

// Audience import item (reuses lead import item schema)
export const importAudienceLeadItemSchema = importLeadItemSchema;
export type ImportAudienceLeadItem = z.infer<typeof importAudienceLeadItemSchema>;
